<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div :class="['action-container', 'justify-content-end']">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <div
        v-if="isEdit && selectedStore"
        class="mt-3 font-weight-bold"
      >
        {{ selectedStore.name }}
      </div>

      <generic-form
        ref="form"
        :attributes="formAttributes"
        :form="form"
        :form-template="formTemplate"
        class="mt-3 py-3"
      >
        <template
          v-for="(row, index) in formTemplate"
        >
          <!-- Terms -->
          <div
            v-if="row.key === 'terms'"
            :key="row.key"
            :style="{ order: index }"
          >
            <h4 class="terms-title">{{ $t('table.terms') }}</h4>

            <div
              v-for="lang in getLanguageList(form, row.key)"
              :key="`editor-${lang.key}`"
              class="editor-wrapper pt-2"
            >
              <label>{{ lang.key }}</label>
              <tiptap-editor
                v-if="shouldMountEditor"
                :active-buttons="['bold', 'italic', 'underline',
                                  'paragraph', 'h1', 'h2', 'h3', 'bullet_list',
                                  'ordered_list', 'horizontal_rule', 'undo', 'redo']"
                :initial-content="form[`${row.key}${lang.suffix}`]"
                class="mt-3"
                @update="(content) => handleTermsUpdate(lang, content)"
              />
            </div>
          </div>
          <!-- Terms -->
        </template>
      </generic-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import GenericForm from '../../components/form/Form.vue'
import TiptapEditor from '../../components/tiptap-editor/MainEditor.vue'

import PromoCodeService from '../../api/promo-code'
import VaderService from '../../api/vader'

import VaderUtils from '../../utils/vader'
import {
  ServerToFormData,
  FormToServerFormat,
  FormRules,
  getLanguageList,
} from '../../utils/form'
import {
  getDefaultPromoCodeDetail,
  transformPromoCodeData,
} from '../../const/promo-tag'

export default {
  name: 'PromoCodeDetail',
  components: {
    GenericForm,
    TiptapEditor,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
    store: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    const formRules = {
      required: [FormRules.required],
      priority: [FormRules.id],
      idList: [FormRules.idList],
    }
    return {
      isSaving: false,
      isFetchedForm: false,
      isFetchingCategories: false,
      categories: [],
      isFetchingBrands: false,
      brands: [],
      stores: [],
      storeQuery: '',
      selectedStore: null,
      editor: null,
      form: getDefaultPromoCodeDetail(),
      formRules,
    }
  },
  computed: {
    isLoading() {
      return this.isEdit && !this.isFetchedForm
    },
    isEdit() {
      return !!this.id
    },
    shouldMountEditor() {
      return (this.isEdit && this.isFetchedForm) || !this.isEdit
    },
    formTemplate() {
      const formTemplate = [
        {
          key: 'promo_code',
          title: 'Code',
          isInput: true,
        },
        {
          key: 'enabled',
          isSwitch: true,
        },
        {
          key: 'start_at',
          isDatePicker: true,
          type: 'datetime',
          rules: this.formRules.required,
          custom: {
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
          },
        },
        {
          key: 'end_at',
          isDatePicker: true,
          type: 'datetime',
          rules: this.formRules.required,
          custom: {
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
          },
        },
        {
          key: 'currently_discounted',
          title: 'Promotion Applies to:',
          isCheckbox: true,
          list: [
            'Discounted Items',
            'Regular Price Items',
          ],
        },
        {
          key: 'min_price_usd',
          title: 'Min. price (USD):',
          isInput: true,
          trim: true,
        },
        {
          key: 'max_price_usd',
          title: 'Max. price (USD):',
          isInput: true,
          trim: true,
        },
        {
          key: 'category_slugs',
          isSelect: true,
          list: this.categories,
          optionValue: 'slug',
          optionLabel: 'slug',
          title: this.$t('table.categories'),
          custom: {
            multiple: true,
            filterable: true,
            class: 'w-100',
            loading: this.isFetchingCategories,
          },
        },
        {
          key: 'brand_slugs',
          isSelect: true,
          list: this.brands,
          optionValue: 'slug',
          optionLabel: 'name',
          title: this.$t('table.brands'),
          custom: {
            multiple: true,
            filterable: true,
            class: 'w-100',
            loading: this.isFetchingBrands,
            remoteMethod: this.getBrands,
            remote: true,
          },
        },
        {
          key: 'priority',
          title: 'Priority',
          isInput: true,
          rules: this.formRules.priority,
        },
        {
          key: 'whitelist_product_ids',
          isInput: true,
          title: this.$t('table.includeProductIds'),
          rules: this.formRules.idList,
          trim: true,
          custom: {
            type: 'textarea',
          },
        },
        {
          key: 'blacklist_product_ids',
          isInput: true,
          title: this.$t('table.excludeProductIds'),
          rules: this.formRules.idList,
          trim: true,
          custom: {
            type: 'textarea',
          },
        },
        {
          key: 'title',
          isLocalized: true,
        },
        {
          key: 'terms',
          isSlot: true,
        },
      ]
      if (!this.isEdit) {
        formTemplate.unshift({
          key: 'store_id',
          isSelect: true,
          list: this.stores,
          optionValue: 'id',
          optionLabel: 'name',
          rules: this.formRules.required,
          custom: {
            placeholder: this.$t('placeholder.enterStore'),
            remote: true,
            remoteMethod: this.getStoreListByKeyword,
            filterable: true,
          },
        })
      }
      return formTemplate
    },
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '92px',
      }
    },
  },
  created() {
    if (this.store) {
      this.selectedStore = this.store
    }
    if (this.isEdit && !this.selectedStore) {
      this.goToListPage()
      return
    }

    this.getCategories()
    if (this.id) {
      this.getPromoCodeDetail(this.id)
    }
  },
  methods: {
    async getPromoCodeDetail(id) {
      try {
        const { data } = await PromoCodeService.getPromoTag(id)
        this.form = {
          ...transformPromoCodeData(ServerToFormData.convert(data)),
          store: '',
        }
      } finally {
        this.isFetchedForm = true
      }
    },
    async updatePromoCodeDetail(form, id) {
      this.isSaving = true
      try {
        if (id) {
          await PromoCodeService.updatePromoTag(form, id)
        } else {
          await PromoCodeService.createPromoTag(form)
        }
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async getStoreListByKeyword(query) {
      if (query) {
        try {
          const { data } = await VaderService.getStores(query)
          this.stores = VaderUtils.handleStoreNameCollision(data.data)
        } catch (error) {
          console.error(error);
        }
      } else {
        this.stores = [];
      }
    },
    async getCategories() {
      this.isFetchingCategories = true
      try {
        const { data: { data: categoriesData } } = await VaderService.getCategories()
        this.categories = VaderUtils.reduceFlattenedCategoryTree(VaderUtils.flattenCategoryTree(categoriesData))
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetchingCategories = false
      }
    },
    async getBrands(keyword) {
      if (!keyword) return
      this.isFetchingBrands = true
      try {
        const { data: { data: brandsData } } = await VaderService.getBrands(keyword)
        this.brands = brandsData
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetchingBrands = false
      }
    },
    goToListPage() {
      if (!this.selectedStore && this.form.store_id) {
        this.selectedStore = this.stores.find(s => s.id === this.form.store_id)
      }
      const params = { store: this.selectedStore }
      this.$router.push({ name: 'PromotionCodeList', params })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    handleCancel() {
      this.goToListPage()
    },
    handleSave() {
      this.$refs.form.$refs.form.validate().then(() => {
        const form = FormToServerFormat.convert(this.form)
        form.priority = Number(form.priority) || 0
        form.product_with_discount = Number(form.currently_discounted.includes('Discounted Items'))
        form.product_without_discount = Number(form.currently_discounted.includes('Regular Price Items'))
        const { id } = this.form
        this.updatePromoCodeDetail(form, id)
      }).catch(() => {
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
    handleTermsUpdate({ suffix }, content) {
      this.form[`terms${suffix}`] = content
    },
    getLanguageList,
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

.terms-title {
  margin-top: 0;
  border-bottom: 1px solid #e5e5e5;
  padding: 5px 0 5px 5px;
  margin-bottom: 8px;
}

.editor {
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 8px;
  margin-top: 0 !important;
}

.editor-wrapper {
  margin-left: 6px;
  &:nth-of-type(2n) {
    background-color: #f8f8f8;
  }
}

::v-deep {
  .el-select {
    min-width: 240px;
  }
}
</style>
